import { useMemo } from 'react';

interface CustomRoom {
  id: string;
  name: string;
  count: number;
}

interface FloorPlanConfig {
  bedrooms: number;
  bathrooms: number;
  livingRooms: number;
  customRooms: CustomRoom[];
  area: number;
  unit: string;
  kitchenType: string;
  extras: string[];
  prompt: string;
}

interface Room {
  id: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  textColor: string;
  icon: string;
  subLabel?: string;
}

interface Door {
  x: number;
  y: number;
  rotation: number;
  side: 'top' | 'bottom' | 'left' | 'right';
}

interface Window {
  x: number;
  y: number;
  horizontal: boolean;
}

function buildFloorPlan(config: FloorPlanConfig) {
  const rooms: Room[] = [];
  const doors: Door[] = [];
  const windows: Window[] = [];

  const totalBeds = config.bedrooms;
  const totalBaths = config.bathrooms;
  const totalLiving = config.livingRooms || 1;
  const hasGarage = config.extras.includes('Garage');
  const hasPool = config.extras.includes('Pool');
  const hasBasement = config.extras.includes('Basement');
  const hasOffice = config.extras.includes('Home Office');
  const hasGym = config.extras.includes('Gym');
  const hasLaundry = config.extras.includes('Laundry Room');
  const hasBalcony = config.extras.includes('Balcony');
  const hasTerrace = config.extras.includes('Terrace');
  const hasGarden = config.extras.includes('Garden');
  const kitchenType = config.kitchenType;

  // Canvas: 800 x 700 internal units
  const CW = 800;
  const CH = 700;

  // ── ZONE LAYOUT ──
  // Left column: garage (if any) + bedrooms
  // Center: living/dining/kitchen
  // Right: bathrooms + extras

  const WALL = 8;
  const PAD = 4;

  // Determine column widths
  const leftW = hasGarage ? 200 : 180;
  const rightW = 160;
  const centerW = CW - leftW - rightW - WALL * 4;

  // ── ENTRY / FOYER ──
  const foyerH = 70;
  rooms.push({
    id: 'foyer',
    label: 'Foyer',
    x: leftW + WALL * 2,
    y: CH - foyerH - WALL,
    w: centerW,
    h: foyerH,
    color: '#F0FDF4',
    textColor: '#166534',
    icon: '⬛',
    subLabel: 'Entry',
  });

  // Front door
  doors.push({
    x: leftW + WALL * 2 + centerW / 2,
    y: CH - WALL,
    rotation: 0,
    side: 'bottom',
  });

  // ── LIVING ROOMS ──
  const livingH = totalLiving === 1 ? 160 : 120;
  for (let i = 0; i < Math.min(totalLiving, 2); i++) {
    const yPos = CH - foyerH - WALL * 2 - livingH - i * (livingH + WALL);
    rooms.push({
      id: `living-${i}`,
      label: i === 0 ? 'Living Room' : 'Family Room',
      x: leftW + WALL * 2,
      y: yPos,
      w: kitchenType === 'open' ? centerW * 0.6 : centerW,
      h: livingH,
      color: '#EFF6FF',
      textColor: '#1E40AF',
      icon: '🛋',
    });
    // Windows on living room
    windows.push({ x: leftW + WALL * 2 + centerW * 0.3, y: yPos, horizontal: true });
  }

  // ── KITCHEN ──
  const kitchenY = CH - foyerH - WALL * 2 - livingH - WALL;
  const kitchenW = kitchenType === 'open' ? centerW * 0.4 : centerW * 0.5;
  const kitchenH = kitchenType === 'galley' ? 80 : 120;
  rooms.push({
    id: 'kitchen',
    label: kitchenType === 'island' ? 'Kitchen (Island)' : kitchenType === 'galley' ? 'Kitchen (Galley)' : kitchenType === 'open' ? 'Kitchen (Open)' : 'Kitchen',
    x: kitchenType === 'open' ? leftW + WALL * 2 + centerW * 0.6 : leftW + WALL * 2,
    y: kitchenY,
    w: kitchenW,
    h: kitchenH,
    color: '#FFFBEB',
    textColor: '#92400E',
    icon: '🍳',
  });

  // ── DINING ──
  if (kitchenType !== 'open') {
    rooms.push({
      id: 'dining',
      label: 'Dining Room',
      x: leftW + WALL * 2 + kitchenW + WALL,
      y: kitchenY,
      w: centerW - kitchenW - WALL,
      h: kitchenH,
      color: '#FFF7ED',
      textColor: '#9A3412',
      icon: '🍽',
    });
  }

  // ── BEDROOMS ──
  const bedroomH = Math.min(130, Math.max(90, Math.floor((CH - 80) / Math.max(totalBeds, 1))));
  const bedroomW = leftW - WALL * 2;
  for (let i = 0; i < Math.min(totalBeds, 6); i++) {
    const yPos = WALL + i * (bedroomH + WALL);
    const isMaster = i === 0;
    rooms.push({
      id: `bedroom-${i}`,
      label: isMaster ? 'Master Bedroom' : `Bedroom ${i + 1}`,
      x: WALL,
      y: yPos,
      w: bedroomW,
      h: bedroomH,
      color: isMaster ? '#F5F3FF' : '#FAF5FF',
      textColor: isMaster ? '#4C1D95' : '#6B21A8',
      icon: '🛏',
      subLabel: isMaster ? 'Master' : undefined,
    });
    // Bedroom windows
    windows.push({ x: WALL, y: yPos + bedroomH / 2, horizontal: false });
    // Bedroom doors
    doors.push({
      x: bedroomW + WALL,
      y: yPos + bedroomH - 20,
      rotation: 90,
      side: 'right',
    });
  }

  // ── BATHROOMS ──
  const bathH = Math.min(100, Math.max(70, Math.floor((CH - 80) / Math.max(totalBaths, 1))));
  const bathW = rightW - WALL * 2;
  for (let i = 0; i < Math.min(totalBaths, 5); i++) {
    const yPos = WALL + i * (bathH + WALL);
    const isEnsuite = i === 0;
    rooms.push({
      id: `bath-${i}`,
      label: isEnsuite ? 'En-Suite' : i === 1 ? 'Main Bath' : `Bathroom ${i + 1}`,
      x: CW - rightW + WALL,
      y: yPos,
      w: bathW,
      h: bathH,
      color: '#ECFEFF',
      textColor: '#164E63',
      icon: '🚿',
    });
    doors.push({
      x: CW - rightW + WALL,
      y: yPos + bathH - 15,
      rotation: 270,
      side: 'left',
    });
  }

  // ── EXTRAS ──
  let extraY = WALL + Math.min(totalBaths, 5) * (bathH + WALL) + WALL;

  if (hasOffice) {
    rooms.push({
      id: 'office',
      label: 'Home Office',
      x: CW - rightW + WALL,
      y: extraY,
      w: bathW,
      h: 90,
      color: '#F0FDF4',
      textColor: '#14532D',
      icon: '💻',
    });
    extraY += 90 + WALL;
  }

  if (hasGym) {
    rooms.push({
      id: 'gym',
      label: 'Gym',
      x: CW - rightW + WALL,
      y: extraY,
      w: bathW,
      h: 90,
      color: '#FFF1F2',
      textColor: '#881337',
      icon: '🏋',
    });
    extraY += 90 + WALL;
  }

  if (hasLaundry) {
    rooms.push({
      id: 'laundry',
      label: 'Laundry',
      x: CW - rightW + WALL,
      y: extraY,
      w: bathW,
      h: 70,
      color: '#F0F9FF',
      textColor: '#0C4A6E',
      icon: '🧺',
    });
    extraY += 70 + WALL;
  }

  // ── GARAGE ──
  if (hasGarage) {
    rooms.push({
      id: 'garage',
      label: 'Garage',
      x: WALL,
      y: CH - 130 - WALL,
      w: leftW - WALL * 2,
      h: 130,
      color: '#F9FAFB',
      textColor: '#374151',
      icon: '🚗',
    });
    // Garage door
    doors.push({
      x: WALL + (leftW - WALL * 2) / 2,
      y: CH - WALL,
      rotation: 0,
      side: 'bottom',
    });
  }

  // ── CUSTOM ROOMS ──
  let customX = leftW + WALL * 2;
  let customY = WALL;
  const customRoomH = 90;
  const customRoomW = Math.min(160, centerW / 2);

  (config.customRooms || []).forEach((cr, idx) => {
    for (let c = 0; c < Math.min(cr.count, 2); c++) {
      rooms.push({
        id: `custom-${cr.id}-${c}`,
        label: cr.count > 1 ? `${cr.name} ${c + 1}` : cr.name,
        x: customX + (idx % 2) * (customRoomW + WALL),
        y: customY + Math.floor(idx / 2) * (customRoomH + WALL),
        w: customRoomW,
        h: customRoomH,
        color: '#FDF4FF',
        textColor: '#701A75',
        icon: '✨',
      });
    }
  });

  // ── POOL (outside) ──
  if (hasPool) {
    rooms.push({
      id: 'pool',
      label: 'Swimming Pool',
      x: leftW + WALL * 2,
      y: WALL,
      w: centerW * 0.7,
      h: 80,
      color: '#CFFAFE',
      textColor: '#164E63',
      icon: '🏊',
    });
  }

  // ── BALCONY / TERRACE ──
  if (hasBalcony || hasTerrace) {
    rooms.push({
      id: 'balcony',
      label: hasBalcony ? 'Balcony' : 'Terrace',
      x: leftW + WALL * 2 + centerW * 0.1,
      y: WALL,
      w: centerW * 0.8,
      h: 55,
      color: '#F0FDF4',
      textColor: '#14532D',
      icon: '🌿',
    });
  }

  // ── HALLWAY / CORRIDOR ──
  // Add a corridor connecting bedrooms to living area
  rooms.push({
    id: 'hallway',
    label: 'Hallway',
    x: leftW,
    y: WALL,
    w: WALL * 2,
    h: CH - WALL * 2,
    color: '#F8FAFC',
    textColor: '#64748B',
    icon: '',
  });

  return { rooms, doors, windows, CW, CH };
}

interface FloorPlanSVGProps {
  config: FloorPlanConfig;
  darkMode: boolean;
  compact?: boolean;
  referenceImageUrl?: string;
  onRoomClick?: (roomId: string, roomLabel: string) => void;
  selectedRoomId?: string;
  favoriteRoomIds?: string[];
  onToggleFavorite?: (roomId: string) => void;
}

export default function FloorPlanSVG({ config, darkMode, compact = false, referenceImageUrl, onRoomClick, selectedRoomId, favoriteRoomIds = [], onToggleFavorite }: FloorPlanSVGProps) {
  const { rooms, doors, windows, CW, CH } = useMemo(() => buildFloorPlan(config), [config]);

  const bgColor = darkMode ? '#1F2937' : '#FFFFFF';
  const wallColor = darkMode ? '#94A3B8' : '#1E293B';
  const gridColor = darkMode ? '#374151' : '#E2E8F0';
  const labelBg = darkMode ? 'rgba(15,23,42,0.7)' : 'rgba(255,255,255,0.85)';
  const compassColor = darkMode ? '#94A3B8' : '#475569';

  const scale = compact ? 0.55 : 1;
  const vw = CW * scale;
  const vh = CH * scale;

  // Area label
  const areaLabel = `${config.area} ${config.unit === 'sqft' ? 'sq ft' : 'sq m'}`;

  return (
    <div className="relative w-full h-full flex flex-col">
      {/* Reference image indicator */}
      {referenceImageUrl && (
        <div className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold ${darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
          <i className="ri-image-line"></i>
          Generating similar plan based on your reference image
        </div>
      )}

      <div className="flex-1 overflow-auto flex items-center justify-center p-4">
        <div className="relative" style={{ width: vw, height: vh }}>
          <svg
            width={vw}
            height={vh}
            viewBox={`0 0 ${CW} ${CH}`}
            xmlns="http://www.w3.org/2000/svg"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            {/* Background */}
            <rect width={CW} height={CH} fill={bgColor} />

            {/* Grid */}
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d={`M 40 0 L 0 0 0 40`} fill="none" stroke={gridColor} strokeWidth="0.5" />
              </pattern>
              <filter id="shadow" x="-5%" y="-5%" width="110%" height="110%">
                <feDropShadow dx="1" dy="1" stdDeviation="2" floodOpacity="0.15" />
              </filter>
            </defs>
            <rect width={CW} height={CH} fill="url(#grid)" />

            {/* Outer building boundary */}
            <rect
              x={8} y={8}
              width={CW - 16} height={CH - 16}
              fill="none"
              stroke={wallColor}
              strokeWidth="6"
              rx="2"
            />

            {/* Rooms */}
            {rooms.map((room) => {
              const isSelected = selectedRoomId === room.id;
              const isClickable = !!onRoomClick && room.id !== 'hallway';
              return (
              <g
                key={room.id}
                filter="url(#shadow)"
                onClick={() => isClickable && onRoomClick && onRoomClick(room.id, room.label)}
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
              >
                {/* Star favorite badge — top-right corner of room */}
                {isClickable && favoriteRoomIds.includes(room.id) && (
                  <g
                    transform={`translate(${room.x + room.w - 14}, ${room.y + 6})`}
                    onClick={(e) => { e.stopPropagation(); onToggleFavorite && onToggleFavorite(room.id); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle cx="0" cy="0" r="8" fill="#F43F5E" opacity="0.9" />
                    <text x="0" y="3.5" textAnchor="middle" fontSize="9" fill="white">★</text>
                  </g>
                )}
                {/* Room fill */}
                <rect
                  x={room.x}
                  y={room.y}
                  width={room.w}
                  height={room.h}
                  fill={darkMode ? room.color.replace('#', '#') + '22' : room.color}
                  stroke={isSelected ? '#14B8A6' : wallColor}
                  strokeWidth={isSelected ? 4 : room.id === 'hallway' ? 1 : 3}
                  rx="1"
                  opacity={isSelected ? 1 : 0.95}
                />
                {/* Selected highlight overlay */}
                {isSelected && (
                  <rect
                    x={room.x + 2} y={room.y + 2}
                    width={room.w - 4} height={room.h - 4}
                    fill="#14B8A6"
                    fillOpacity="0.12"
                    rx="1"
                  />
                )}

                {/* Room label background */}
                {room.label && room.w > 50 && room.h > 40 && (
                  <>
                    <rect
                      x={room.x + room.w / 2 - Math.min(room.label.length * 4.5, room.w - 10) / 2}
                      y={room.y + room.h / 2 - 18}
                      width={Math.min(room.label.length * 4.5 + 8, room.w - 10)}
                      height={room.subLabel ? 32 : 20}
                      fill={labelBg}
                      rx="3"
                    />
                    <text
                      x={room.x + room.w / 2}
                      y={room.y + room.h / 2 - 4}
                      textAnchor="middle"
                      fontSize={room.w < 100 ? 9 : 11}
                      fontWeight="700"
                      fill={darkMode ? '#E2E8F0' : room.textColor}
                    >
                      {room.label}
                    </text>
                    {room.subLabel && (
                      <text
                        x={room.x + room.w / 2}
                        y={room.y + room.h / 2 + 10}
                        textAnchor="middle"
                        fontSize={8}
                        fontWeight="500"
                        fill={darkMode ? '#94A3B8' : '#64748B'}
                      >
                        {room.subLabel}
                      </text>
                    )}
                  </>
                )}

                {/* Dimension lines */}
                {room.w > 80 && room.h > 60 && (
                  <>
                    {/* Width dimension */}
                    <line
                      x1={room.x + 6} y1={room.y + room.h - 8}
                      x2={room.x + room.w - 6} y2={room.y + room.h - 8}
                      stroke={darkMode ? '#475569' : '#94A3B8'}
                      strokeWidth="0.8"
                      strokeDasharray="3,2"
                    />
                    <text
                      x={room.x + room.w / 2}
                      y={room.y + room.h - 2}
                      textAnchor="middle"
                      fontSize={7}
                      fill={darkMode ? '#64748B' : '#94A3B8'}
                    >
                      {Math.round(room.w / 10)}m
                    </text>
                  </>
                )}
              </g>
              );
            })}

            {/* Doors */}
            {doors.map((door, i) => (
              <g key={`door-${i}`} transform={`translate(${door.x}, ${door.y}) rotate(${door.rotation})`}>
                {/* Door opening gap */}
                <line x1="-15" y1="0" x2="15" y2="0" stroke={bgColor} strokeWidth="6" />
                {/* Door arc */}
                <path
                  d="M -15 0 A 15 15 0 0 1 0 -15"
                  fill="none"
                  stroke={darkMode ? '#60A5FA' : '#3B82F6'}
                  strokeWidth="1.5"
                  strokeDasharray="3,2"
                />
                {/* Door panel */}
                <line x1="-15" y1="0" x2="0" y2="-15" stroke={darkMode ? '#60A5FA' : '#3B82F6'} strokeWidth="1.5" />
              </g>
            ))}

            {/* Windows */}
            {windows.map((win, i) => (
              <g key={`win-${i}`}>
                {win.horizontal ? (
                  <>
                    <rect x={win.x - 20} y={win.y - 3} width={40} height={6} fill={bgColor} />
                    <line x1={win.x - 20} y1={win.y} x2={win.x + 20} y2={win.y} stroke={darkMode ? '#7DD3FC' : '#0EA5E9'} strokeWidth="3" />
                    <line x1={win.x - 20} y1={win.y - 3} x2={win.x - 20} y2={win.y + 3} stroke={darkMode ? '#7DD3FC' : '#0EA5E9'} strokeWidth="1.5" />
                    <line x1={win.x + 20} y1={win.y - 3} x2={win.x + 20} y2={win.y + 3} stroke={darkMode ? '#7DD3FC' : '#0EA5E9'} strokeWidth="1.5" />
                  </>
                ) : (
                  <>
                    <rect x={win.x - 3} y={win.y - 20} width={6} height={40} fill={bgColor} />
                    <line x1={win.x} y1={win.y - 20} x2={win.x} y2={win.y + 20} stroke={darkMode ? '#7DD3FC' : '#0EA5E9'} strokeWidth="3" />
                    <line x1={win.x - 3} y1={win.y - 20} x2={win.x + 3} y2={win.y - 20} stroke={darkMode ? '#7DD3FC' : '#0EA5E9'} strokeWidth="1.5" />
                    <line x1={win.x - 3} y1={win.y + 20} x2={win.x + 3} y2={win.y + 20} stroke={darkMode ? '#7DD3FC' : '#0EA5E9'} strokeWidth="1.5" />
                  </>
                )}
              </g>
            ))}

            {/* Compass Rose */}
            <g transform={`translate(${CW - 40}, 40)`}>
              <circle cx="0" cy="0" r="18" fill={darkMode ? '#1E293B' : '#F8FAFC'} stroke={compassColor} strokeWidth="1" />
              {/* N */}
              <polygon points="0,-14 -5,-4 5,-4" fill={darkMode ? '#F87171' : '#EF4444'} />
              {/* S */}
              <polygon points="0,14 -5,4 5,4" fill={compassColor} />
              {/* E */}
              <polygon points="14,0 4,-5 4,5" fill={compassColor} />
              {/* W */}
              <polygon points="-14,0 -4,-5 -4,5" fill={compassColor} />
              <text x="0" y="-6" textAnchor="middle" fontSize="7" fontWeight="800" fill={darkMode ? '#F87171' : '#EF4444'}>N</text>
            </g>

            {/* Scale bar */}
            <g transform={`translate(20, ${CH - 30})`}>
              <rect x="0" y="0" width="60" height="8" fill="none" stroke={compassColor} strokeWidth="1" />
              <rect x="0" y="0" width="30" height="8" fill={compassColor} />
              <text x="0" y="18" fontSize="7" fill={compassColor}>0</text>
              <text x="25" y="18" fontSize="7" fill={compassColor}>5m</text>
              <text x="52" y="18" fontSize="7" fill={compassColor}>10m</text>
            </g>

            {/* Title block */}
            <g transform={`translate(${CW / 2}, ${CH - 18})`}>
              <rect x="-120" y="-10" width="240" height="16" fill={darkMode ? '#1E293B' : '#F1F5F9'} rx="3" />
              <text x="0" y="2" textAnchor="middle" fontSize="8" fontWeight="700" fill={darkMode ? '#94A3B8' : '#475569'}>
                {config.bedrooms}BR · {config.bathrooms}BA · {areaLabel} · {config.kitchenType.charAt(0).toUpperCase() + config.kitchenType.slice(1)} Kitchen
              </text>
            </g>

            {/* Legend */}
            <g transform={`translate(${CW - 155}, ${CH - 120})`}>
              <rect x="0" y="0" width="145" height="110" fill={darkMode ? 'rgba(15,23,42,0.8)' : 'rgba(248,250,252,0.9)'} rx="4" stroke={darkMode ? '#334155' : '#E2E8F0'} strokeWidth="1" />
              <text x="8" y="14" fontSize="8" fontWeight="700" fill={darkMode ? '#94A3B8' : '#475569'}>LEGEND</text>
              {[
                { color: '#EFF6FF', label: 'Living Areas' },
                { color: '#F5F3FF', label: 'Bedrooms' },
                { color: '#ECFEFF', label: 'Bathrooms' },
                { color: '#FFFBEB', label: 'Kitchen' },
                { color: '#F9FAFB', label: 'Utility / Garage' },
              ].map((item, i) => (
                <g key={item.label} transform={`translate(8, ${22 + i * 17})`}>
                  <rect x="0" y="0" width="12" height="10" fill={item.color} stroke={wallColor} strokeWidth="0.8" />
                  <text x="17" y="9" fontSize="8" fill={darkMode ? '#94A3B8' : '#475569'}>{item.label}</text>
                </g>
              ))}
            </g>
          </svg>
        </div>
      </div>

      {/* Bottom info bar */}
      <div className={`flex items-center justify-between px-4 py-2 text-xs border-t ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
        <span className="flex items-center gap-2">
          <i className="ri-layout-grid-line text-teal-500"></i>
          <strong className={darkMode ? 'text-teal-400' : 'text-teal-700'}>2D Floor Plan</strong>
          · {config.bedrooms} Bedrooms · {config.bathrooms} Bathrooms · {config.livingRooms || 1} Living
        </span>
        <span className="flex items-center gap-2">
          <i className="ri-ruler-line"></i>
          {areaLabel} total
          {config.extras.length > 0 && (
            <span className={`px-2 py-0.5 rounded-full font-semibold ${darkMode ? 'bg-teal-900/40 text-teal-400' : 'bg-teal-100 text-teal-700'}`}>
              +{config.extras.length} extras
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
